<template>
  <div class="roles-permissions-container" style="padding: 20px;">
    <el-card shadow="never">
      <div slot="header" class="clearfix">
        <span style="font-size: 18px; font-weight: bold; color: #2c3e50;">
          <i class="fa fa-key" style="margin-right: 8px;"></i>Gestión y Validación de Roles y Permisos
        </span>
        <el-tag type="danger" style="margin-left: 15px;" v-if="isSuperAdmin">SuperAdministrador</el-tag>
      </div>

      <el-alert
        v-if="!isSuperAdmin"
        title="Acceso Restringido"
        type="warning"
        description="Esta pantalla solo está disponible para usuarios con perfil SuperAdministrador."
        show-icon
        :closable="false">
      </el-alert>

      <div v-else>
        <el-tabs v-model="activeTab" type="card">
          <!-- TAB 1: GESTIÓN DE ROLES -->
          <el-tab-pane label="Gestión de Roles" name="roles">
            <div style="margin-bottom: 20px; display: flex; justify-content: space-between; align-items: center;">
              <div>
                <el-input
                  placeholder="Buscar rol..."
                  v-model="searchRole"
                  prefix-icon="el-icon-search"
                  clearable
                  style="width: 300px;" size="small">
                </el-input>
              </div>
              <el-button type="primary" icon="el-icon-plus" size="small" @click="handleCreateRole">
                Nuevo Rol
              </el-button>
            </div>

            <el-table
              :data="filteredRoles"
              v-loading="loadingRoles"
              border
              stripe
              style="width: 100%">
              <el-table-column prop="name" label="Nombre del Rol" min-width="180">
                <template slot-scope="scope">
                  <strong style="color: #2c3e50; font-size: 14px;">{{ scope.row.name }}</strong>
                  <el-tag v-if="scope.row.is_system" type="danger" size="mini" style="margin-left: 8px;">Sistema</el-tag>
                </template>
              </el-table-column>

              <el-table-column prop="permissions_count" label="Permisos Asignados" width="160" align="center">
                <template slot-scope="scope">
                  <el-tag type="info" size="medium">
                    <i class="fa fa-lock"></i> {{ scope.row.permissions_count }} permisos
                  </el-tag>
                </template>
              </el-table-column>

              <el-table-column prop="users_count" label="Usuarios Vinculados" width="160" align="center">
                <template slot-scope="scope">
                  <el-tag type="success" size="medium">
                    <i class="fa fa-users"></i> {{ scope.row.users_count }} usuarios
                  </el-tag>
                </template>
              </el-table-column>

              <el-table-column prop="created_at" label="Fecha de Creación" width="180" align="center">
                <template slot-scope="scope">
                  <span>{{ scope.row.created_at || 'Sistema' }}</span>
                </template>
              </el-table-column>

              <el-table-column label="Acciones" width="220" align="center">
                <template slot-scope="scope">
                  <el-button
                    type="primary"
                    icon="el-icon-edit"
                    size="mini"
                    title="Editar Permisos"
                    @click="handleEditRole(scope.row)">
                    Editar
                  </el-button>
                  <el-button
                    type="danger"
                    icon="el-icon-delete"
                    size="mini"
                    title="Eliminar Rol"
                    :disabled="scope.row.is_system || scope.row.users_count > 0"
                    @click="handleDeleteRole(scope.row)">
                    Eliminar
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
          </el-tab-pane>

          <!-- TAB 2: VALIDACIÓN DE PERMISOS -->
          <el-tab-pane label="Validación y Auditoría de Permisos" name="validator">
            <div style="background-color: #f8f9fa; padding: 15px; border-radius: 4px; margin-bottom: 20px; border: 1px solid #e9ecef;">
              <el-row :gutter="20" align="middle">
                <el-col :span="6">
                  <label style="display: block; margin-bottom: 5px; font-weight: bold; font-size: 13px; color: #606266;">
                    Modo de Inspección:
                  </label>
                  <el-radio-group v-model="inspectionMode" size="small" @change="onInspectionModeChange">
                    <el-radio-button label="role">Por Rol</el-radio-button>
                    <el-radio-button label="user">Por Usuario</el-radio-button>
                  </el-radio-group>
                </el-col>

                <el-col :span="9" v-if="inspectionMode === 'role'">
                  <label style="display: block; margin-bottom: 5px; font-weight: bold; font-size: 13px; color: #606266;">
                    Seleccionar Rol:
                  </label>
                  <el-select v-model="selectedRoleId" filterable placeholder="Seleccione un rol" size="small" style="width: 100%;" @change="inspectRole">
                    <el-option
                      v-for="r in rolesList"
                      :key="r.id"
                      :label="r.name + ' (' + r.permissions_count + ' permisos)'"
                      :value="r.id">
                    </el-option>
                  </el-select>
                </el-col>

                <el-col :span="9" v-if="inspectionMode === 'user'">
                  <label style="display: block; margin-bottom: 5px; font-weight: bold; font-size: 13px; color: #606266;">
                    Seleccionar Usuario:
                  </label>
                  <el-select v-model="selectedUserId" filterable placeholder="Buscar usuario por nombre o email" size="small" style="width: 100%;" @change="inspectUser">
                    <el-option
                      v-for="u in usersList"
                      :key="u.id"
                      :label="u.name + ' (' + u.email + ' - ' + u.profile + ')'"
                      :value="u.id">
                    </el-option>
                  </el-select>
                </el-col>

                <el-col :span="9">
                  <label style="display: block; margin-bottom: 5px; font-weight: bold; font-size: 13px; color: #606266;">
                    Filtrar Permisos:
                  </label>
                  <el-input
                    placeholder="Filtrar por nombre de permiso..."
                    v-model="inspectionSearch"
                    prefix-icon="el-icon-search"
                    clearable
                    size="small">
                  </el-input>
                </el-col>
              </el-row>
            </div>

            <!-- Inspect Summary Badge Header -->
            <div v-if="inspectedData" style="margin-bottom: 20px; padding: 12px 18px; background: #eef5fe; border-left: 4px solid #409EFF; border-radius: 4px;">
              <div v-if="inspectionMode === 'role'">
                <h4 style="margin: 0 0 5px 0; color: #2c3e50;">
                  Rol Evaluado: <strong>{{ inspectedData.name }}</strong>
                </h4>
                <p style="margin: 0; color: #606266; font-size: 13px;">
                  Total permisos activos: <strong>{{ inspectedData.permissions ? inspectedData.permissions.length : 0 }}</strong>
                </p>
              </div>

              <div v-if="inspectionMode === 'user'">
                <h4 style="margin: 0 0 5px 0; color: #2c3e50;">
                  Usuario Evaluado: <strong>{{ inspectedData.user.name }}</strong> ({{ inspectedData.user.email }})
                </h4>
                <p style="margin: 0; color: #606266; font-size: 13px;">
                  Perfil / Roles: <strong>{{ inspectedData.user.profile }}</strong> |
                  Permisos Totales Efectivos: <strong>{{ inspectedData.all_permissions.length }}</strong> |
                  Permisos Heredados por Rol: <strong>{{ inspectedData.role_permissions.length }}</strong> |
                  Permisos Directos: <strong>{{ inspectedData.direct_permissions.length }}</strong>
                </p>
              </div>
            </div>

            <!-- Permission Tree Breakdown -->
            <div v-loading="loadingInspection">
              <el-row :gutter="20">
                <el-col :span="12" v-for="cat in filteredGroupedPermissions" :key="cat.key" style="margin-bottom: 20px;">
                  <el-card shadow="hover" body-style="padding: 15px;">
                    <div slot="header" class="clearfix" style="background-color: #f5f7fa; padding: 8px 12px; margin: -15px -15px 15px -15px; border-bottom: 1px solid #ebedf0;">
                      <strong style="color: #34495e; font-size: 14px;">
                        <i class="fa fa-folder-open" style="margin-right: 6px; color: #409EFF;"></i>{{ cat.label }}
                      </strong>
                      <span class="pull-right badge" style="background: #909399; font-weight: normal;">
                        {{ cat.permissions.length }} ítems
                      </span>
                    </div>

                    <div style="display: flex; flex-wrap: wrap; gap: 8px;">
                      <div v-for="perm in cat.permissions" :key="perm.id" style="width: 100%;">
                        <div style="display: flex; justify-content: space-between; align-items: center; padding: 6px 10px; border-radius: 4px; background: #fafafa; border: 1px solid #f0f0f0;">
                          <span style="font-size: 13px; color: #2c3e50;">
                            <strong>{{ perm.title || perm.name }}</strong> <small style="color: #909399;">({{ perm.name }})</small>
                          </span>
                          <span>
                            <el-tag v-if="getPermissionStatus(perm.name) === 'assigned'" type="success" size="mini">
                              <i class="el-icon-check"></i> Asignado
                            </el-tag>
                            <el-tag v-else-if="getPermissionStatus(perm.name) === 'direct'" type="primary" size="mini">
                              <i class="el-icon-user"></i> Directo
                            </el-tag>
                            <el-tag v-else-if="getPermissionStatus(perm.name) === 'role'" type="success" size="mini">
                              <i class="el-icon-s-custom"></i> Heredado por Rol
                            </el-tag>
                            <el-tag v-else type="info" size="mini" style="opacity: 0.5;">
                              Sin Acceso
                            </el-tag>
                          </span>
                        </div>
                      </div>
                    </div>
                  </el-card>
                </el-col>
              </el-row>
            </div>
          </el-tab-pane>
        </el-tabs>
      </div>
    </el-card>

    <!-- DIALOG FORM FOR ROLE CREATE / EDIT -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="70%"
      top="5vh"
      :close-on-click-modal="false">
      <el-form :model="roleForm" :rules="roleRules" ref="roleForm" label-width="120px">
        <el-form-item label="Nombre del Rol" prop="name">
          <el-input v-model="roleForm.name" placeholder="Ej: Supervisor_Turno, Auditor, Contador..." size="small" :disabled="roleForm.is_system"></el-input>
        </el-form-item>

        <div style="margin-top: 15px; margin-bottom: 15px;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
            <h4 style="margin: 0; color: #2c3e50; font-size: 15px;">
              <i class="fa fa-check-square-o" style="margin-right: 6px;"></i>Matriz de Permisos por Módulo:
            </h4>
            <div>
              <el-button type="text" size="small" @click="selectAllPermissions">Marcar Todos</el-button>
              <el-divider direction="vertical"></el-divider>
              <el-button type="text" size="small" @click="deselectAllPermissions">Desmarcar Todos</el-button>
            </div>
          </div>

          <div style="max-height: 450px; overflow-y: auto; padding-right: 10px;">
            <div v-for="cat in groupedPermissions" :key="cat.key" style="margin-bottom: 18px; border: 1px solid #e4e7ed; border-radius: 4px;">
              <div style="background-color: #f5f7fa; padding: 8px 15px; border-bottom: 1px solid #e4e7ed; display: flex; justify-content: space-between; align-items: center;">
                <strong style="color: #303133; font-size: 13px;">{{ cat.label }}</strong>
                <div>
                  <el-checkbox
                    :indeterminate="isCategoryIndeterminate(cat)"
                    v-model="catCheckState[cat.key]"
                    @change="(val) => toggleCategoryPermissions(cat, val)">
                    Seleccionar módulo ({{ getCategorySelectedCount(cat) }}/{{ cat.permissions.length }})
                  </el-checkbox>
                </div>
              </div>

              <div style="padding: 12px 15px; display: flex; flex-wrap: wrap; gap: 10px;">
                <el-checkbox
                  v-for="perm in cat.permissions"
                  :key="perm.id"
                  v-model="roleForm.permissions"
                  :label="perm.name"
                  style="margin-right: 15px; margin-bottom: 10px; width: 46%; white-space: normal; height: auto;">
                  <span style="font-size: 13px; color: #2c3e50;">
                    <strong>{{ perm.title || perm.name }}</strong> <small style="color: #909399;">({{ perm.name }})</small>
                  </span>
                </el-checkbox>
              </div>
            </div>
          </div>
        </div>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-button size="small" @click="dialogVisible = false">Cancelar</el-button>
        <el-button type="primary" size="small" :loading="savingRole" @click="saveRole">Guardar Rol y Permisos</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: "RolesPermissions",
  data() {
    return {
      activeTab: "roles",
      loadingRoles: false,
      rolesList: [],
      searchRole: "",
      
      // Permission list grouped by backend
      groupedPermissions: [],
      
      // Dialog state
      dialogVisible: false,
      dialogTitle: "Nuevo Rol",
      savingRole: false,
      roleForm: {
        id: null,
        name: "",
        permissions: [],
        is_system: false,
      },
      roleRules: {
        name: [{ required: true, message: "El nombre del rol es obligatorio", trigger: "blur" }],
      },
      catCheckState: {},

      // Validator tab state
      inspectionMode: "role",
      selectedRoleId: null,
      selectedUserId: null,
      usersList: [],
      loadingInspection: false,
      inspectedData: null,
      inspectionSearch: "",
    };
  },
  computed: {
    isSuperAdmin() {
      if (!this.$auth || !this.$auth.user) return false;
      const userProf = typeof this.$auth.user.profile === 'object'
        ? (this.$auth.user.profile ? this.$auth.user.profile.profile : null)
        : this.$auth.user.profile;
      const userEmail = typeof this.$auth.user.profile === 'object' && this.$auth.user.profile
        ? this.$auth.user.profile.email
        : (this.$auth.user ? this.$auth.user.email : null);
      return ['SADM', 'SuperAdmin', 'SuperAdministrador', 'SUPER_ADM'].includes(userProf) || userEmail === 'superadminsgp@labortime.com' || (typeof userEmail === 'string' && userEmail.startsWith('soporte@'));
    },
    filteredRoles() {
      if (!this.searchRole) return this.rolesList;
      const term = this.searchRole.toLowerCase();
      return this.rolesList.filter((r) => r.name.toLowerCase().includes(term));
    },
    filteredGroupedPermissions() {
      if (!this.inspectionSearch) return this.groupedPermissions;
      const term = this.inspectionSearch.toLowerCase();
      return this.groupedPermissions.map((cat) => {
        const matchingPerms = cat.permissions.filter((p) => p.name.toLowerCase().includes(term));
        return {
          ...cat,
          permissions: matchingPerms,
        };
      }).filter((cat) => cat.permissions.length > 0);
    },
  },
  mounted() {
    if (this.isSuperAdmin) {
      this.fetchRoles();
      this.fetchPermissionsList();
      this.fetchUsersOptions();
    }
  },
  methods: {
    fetchRoles() {
      this.loadingRoles = true;
      axios
        .get("/api/roles")
        .then((response) => {
          this.rolesList = response.data || [];
          this.loadingRoles = false;
        })
        .catch((err) => {
          this.loadingRoles = false;
          this.$message.error("Error al cargar el listado de roles.");
        });
    },
    fetchPermissionsList() {
      axios
        .get("/api/roles/permissions")
        .then((response) => {
          this.groupedPermissions = response.data || [];
        })
        .catch((err) => {
          this.$message.error("Error al cargar la lista de permisos del sistema.");
        });
    },
    fetchUsersOptions() {
      axios
        .get("/api/users")
        .then((response) => {
          const data = response.data && response.data.data ? response.data.data : response.data;
          let list = data || [];
          if (!this.isSuperAdmin) {
            list = list.filter(u => u.email !== 'superadminsgp@labortime.com' && !['SADM', 'SuperAdmin', 'SuperAdministrador', 'SUPER_ADM'].includes(u.profile) && (!u.email || !u.email.startsWith('soporte@')));
          }
          this.usersList = list;
        })
        .catch(() => {});
    },
    handleCreateRole() {
      this.dialogTitle = "Nuevo Rol";
      this.roleForm = {
        id: null,
        name: "",
        permissions: [],
        is_system: false,
      };
      this.catCheckState = {};
      this.dialogVisible = true;
      if (this.$refs.roleForm) {
        this.$refs.roleForm.clearValidate();
      }
    },
    handleEditRole(role) {
      this.dialogTitle = "Editar Rol: " + role.name;
      this.roleForm = {
        id: role.id,
        name: role.name,
        permissions: [...(role.permissions || [])],
        is_system: role.is_system || false,
      };
      this.catCheckState = {};
      this.dialogVisible = true;
      if (this.$refs.roleForm) {
        this.$refs.roleForm.clearValidate();
      }
    },
    handleDeleteRole(role) {
      this.$confirm(
        "¿Está seguro de que desea eliminar permanentemente el rol '" + role.name + "'?",
        "Confirmar Eliminación",
        {
          confirmButtonText: "Eliminar",
          cancelButtonText: "Cancelar",
          type: "warning",
        }
      )
        .then(() => {
          axios
            .delete("/api/roles/" + role.id)
            .then((res) => {
              this.$message.success(res.data.message || "Rol eliminado.");
              this.fetchRoles();
              if (this.$auth && typeof this.$auth.checkUser === 'function') {
                this.$auth.checkUser();
              }
            })
            .catch((err) => {
              const msg = err.response && err.response.data && err.response.data.message
                ? err.response.data.message
                : "Error al eliminar el rol.";
              this.$message.error(msg);
            });
        })
        .catch(() => {});
    },
    saveRole() {
      this.$refs.roleForm.validate((valid) => {
        if (!valid) return false;
        this.savingRole = true;

        const isEdit = !!this.roleForm.id;
        const url = isEdit ? "/api/roles/" + this.roleForm.id : "/api/roles";
        const method = isEdit ? "put" : "post";

        axios[method](url, {
          name: this.roleForm.name,
          permissions: this.roleForm.permissions,
        })
          .then((res) => {
            this.savingRole = false;
            this.dialogVisible = false;
            this.$message.success(res.data.message || "Rol guardado con éxito.");
            this.fetchRoles();
            if (this.$auth && typeof this.$auth.checkUser === 'function') {
              this.$auth.checkUser();
            }
          })
          .catch((err) => {
            this.savingRole = false;
            const msg = err.response && err.response.data && err.response.data.message
              ? err.response.data.message
              : "Error al guardar el rol.";
            this.$message.error(msg);
          });
      });
    },
    selectAllPermissions() {
      const allNames = [];
      this.groupedPermissions.forEach((cat) => {
        cat.permissions.forEach((p) => allNames.push(p.name));
      });
      this.roleForm.permissions = arrayUnique(allNames);
    },
    deselectAllPermissions() {
      this.roleForm.permissions = [];
    },
    getCategorySelectedCount(cat) {
      const catPermNames = cat.permissions.map((p) => p.name);
      return this.roleForm.permissions.filter((pName) => catPermNames.includes(pName)).length;
    },
    isCategoryIndeterminate(cat) {
      const selectedCount = this.getCategorySelectedCount(cat);
      return selectedCount > 0 && selectedCount < cat.permissions.length;
    },
    toggleCategoryPermissions(cat, isChecked) {
      const catPermNames = cat.permissions.map((p) => p.name);
      if (isChecked) {
        this.roleForm.permissions = arrayUnique([...this.roleForm.permissions, ...catPermNames]);
      } else {
        this.roleForm.permissions = this.roleForm.permissions.filter((pName) => !catPermNames.includes(pName));
      }
    },
    
    // VALIDATOR TAB METHODS
    onInspectionModeChange() {
      this.inspectedData = null;
      this.selectedRoleId = null;
      this.selectedUserId = null;
    },
    inspectRole(roleId) {
      if (!roleId) return;
      this.loadingInspection = true;
      axios
        .get("/api/roles/" + roleId)
        .then((res) => {
          this.inspectedData = res.data;
          this.loadingInspection = false;
        })
        .catch(() => {
          this.loadingInspection = false;
        });
    },
    inspectUser(userId) {
      if (!userId) return;
      this.loadingInspection = true;
      axios
        .get("/api/roles/user-permissions/" + userId)
        .then((res) => {
          this.inspectedData = res.data;
          this.loadingInspection = false;
        })
        .catch(() => {
          this.loadingInspection = false;
        });
    },
    getPermissionStatus(permName) {
      if (!this.inspectedData) return "none";

      if (this.inspectionMode === "role") {
        const assigned = this.inspectedData.permissions || [];
        return assigned.includes(permName) ? "assigned" : "none";
      }

      if (this.inspectionMode === "user") {
        const direct = this.inspectedData.direct_permissions || [];
        const rolePerms = this.inspectedData.role_permissions || [];
        const allPerms = this.inspectedData.all_permissions || [];

        if (direct.includes(permName)) return "direct";
        if (rolePerms.includes(permName)) return "role";
        if (allPerms.includes(permName)) return "assigned";
        return "none";
      }

      return "none";
    },
  },
};

function arrayUnique(array) {
  return Array.from(new Set(array));
}
</script>

<style scoped>
.roles-permissions-container {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}
.badge {
  display: inline-block;
  padding: 3px 8px;
  font-size: 12px;
  color: #fff;
  border-radius: 10px;
}
</style>
